/**
 * PATH: packages/bills/src/components/format-currency.ts
 * FILE_ID: BILLS-PKG-UTIL-MONEY-015
 * PURPOSE: Turn a bill's raw amount + currency code into the headline money
 *          string shown on the summary card (e.g. "A$170.09").
 *
 * WHY: The redesigned data panel leads with the total, so it needs a single,
 *      locale-aware, currency-aware formatter that degrades gracefully. DRF
 *      ships DecimalField as a string, currency may be blank/garbage, and the
 *      amount may be missing entirely — every one of those must produce a sane
 *      output (or null) rather than "NaN" / "$NaN".
 *
 * FLOW: (amount, currency) → parse + validate → Intl.NumberFormat → string|null
 */

/**
 * Format ``amount`` as currency using ``currency`` (a 3-letter ISO code) when
 * it is valid, otherwise as a plain 2dp number. Returns ``null`` when the
 * amount is absent or unparseable so callers can render a placeholder.
 */
export function formatMoney(
  amount: string | number | null | undefined,
  currency?: string | null,
): string | null {
  if (amount == null || amount === '') return null;
  const n =
    typeof amount === 'number' ? amount : Number(String(amount).replace(/,/g, '').trim());
  if (!Number.isFinite(n)) return null;

  const code = (currency ?? '').trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(code)) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
      }).format(n);
    } catch {
      // Unknown ISO code → fall through to the plain-number formatting below.
    }
  }
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}
